import type { Row } from "@tanstack/react-table";

import { Badge, Heading, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@sentricmusic/ui";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { DetailedSong, RecordingData, Song } from "@/api/queries";

import { useGetMyRecordingsDetailed, useGetMySong } from "@/api/queries";
import LoadingSmall from "@/components/LoadingSmall";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SongWriterSplitEntry } from "./SongWriterSplit";

import SongRecording from "./SongRecording";
import SongWriterSplit from "./SongWriterSplit";

type SongAlternativeTitle = {
  title: string;
};

type SongPerformingArtist = {
  isdeleted?: boolean;
  performingartistname: string;
};

// getmywork/getmyworkamendment's embedded recordings — narrower than (and not
// interchangeable with) the RecordingData shape returned by getmyrecordingsdetailed.
type SongWorkRecording = {
  recordingidentifier: string;
  recordingamendmentidentifier?: string;
  isdeleted?: boolean;
  isrc?: string;
  performingartist: string;
  recordlabel?: string;
  releasedate?: string;
  s3presignedurl?: string;
  title: string;
  uicbarcode?: string;
};

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

function createUniqueKey(recording: RecordingData, index: number): string {
  if (recording.recordingidentifier !== "" && recording.recordingidentifier !== EMPTY_GUID) {
    return recording.recordingidentifier;
  }
  return `${recording.uicbarcode ?? ""}_${recording.title}_${recording.performingartist}_${index}`;
}

type SongDetailsProps = {
  row: Row<DetailedSong>;
};

function SongDetails({ row }: SongDetailsProps) {
  const { t } = useTranslation("songs");
  const { original } = row;

  const isTunecore = useUIStateStore(state => state.isTunecore);

  let song: Song | null = null;
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const aac = original.artistaccountcode != null && original.artistaccountcode !== "" ? original.artistaccountcode : selectedAccount;
  const songTemp = useGetMySong({ aac, wc: original.workcode });

  if (songTemp.isSuccess) {
    song = songTemp.data;
  }

  const recordings = useGetMyRecordingsDetailed({ aac, wc: original.workcode });
  useEffect(() => {
    void recordings.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song]);

  let allRecordings: RecordingData[] = [];

  if (recordings.isSuccess && song !== null) {
    const fetchedRecordings = recordings.data.recordings;
    const allSongRecordings = (song.recordings as SongWorkRecording[] | undefined) ?? [];

    // Remove deleted amendments from fetched recordings
    const deletedAmendments = allSongRecordings.filter(
      s => s.recordingidentifier !== "" && (s.isdeleted ?? false),
    );
    const deletedIds = new Set(
      deletedAmendments.map(r => r.recordingidentifier),
    );

    const filteredFetchedRecordings = fetchedRecordings.filter(
      r => !deletedIds.has(r.recordingidentifier),
    );

    // Turn fetched recordings into a Map for deduplication
    // Use a composite key to ensure uniqueness when recordingidentifier is null/undefined/duplicate
    const recordingsMap = new Map<string, RecordingData>(
      filteredFetchedRecordings.map((r, index) => [createUniqueKey(r, index), r]),
    );

    // Process valid (non-deleted) amendments and overwrite or add to the map
    allSongRecordings
      .filter(r => !(r.isdeleted ?? false))
      .forEach((r, index) => {
        const formatted: RecordingData = {
          artistaccountcode: song.artistaccountcode,
          isdeleted: r.isdeleted,
          isrc: r.isrc,
          performingartist: r.performingartist,
          recordingamendmentidentifier: r.recordingamendmentidentifier,
          recordingidentifier: r.recordingidentifier,
          recordlabel: r.recordlabel,
          releasedate: r.releasedate,
          s3presignedurl: r.s3presignedurl,
          title: r.title,
          uicbarcode: r.uicbarcode,
          workcode: original.workcode,
        };

        // Create a unique key for this recording
        const uniqueKey = createUniqueKey(formatted, index + filteredFetchedRecordings.length);

        // Only merge with fetched recording if we have a valid recordingidentifier match
        const hasValidIdentifier = formatted.recordingidentifier !== "" && formatted.recordingidentifier !== EMPTY_GUID;
        const fetched: RecordingData | null = hasValidIdentifier
          ? filteredFetchedRecordings.find(fr => fr.recordingidentifier === formatted.recordingidentifier) ?? null
          : null;

        // Merge objects - fetched provides base data, formatted (amendment) values take priority
        // This ensures amendment s3presignedurl takes precedence over fetched recording's url
        const merged: RecordingData = fetched
          ? {
              ...fetched,
              ...formatted,
              s3presignedurl: (formatted.s3presignedurl != null && formatted.s3presignedurl !== "")
                ? formatted.s3presignedurl
                : fetched.s3presignedurl,
            }
          : formatted;

        // Always add to map with unique key to prevent overwrites
        recordingsMap.set(uniqueKey, merged);
      });

    allRecordings = Array.from(recordingsMap.values());
  }

  if (song === null) {
    return (
      <div className="p-4">
        <LoadingSmall />
      </div>
    );
  }

  const alternativeTitles = (song.alternativetitles as SongAlternativeTitle[] | undefined) ?? [];
  const performingArtists = (song.performingartists as SongPerformingArtist[] | undefined) ?? [];
  const writerSplits = (song.writersplits as SongWriterSplitEntry[] | undefined) ?? [];
  const recordingsCount = (song.recordings as SongWorkRecording[] | undefined)?.length ?? 0;

  return (
    <div
      className={`rounded-lg ${isTunecore ? "bg-canvas-inverse" : "bg-layer-raised"} text-ink-secondary lg:p-6 p-4 mb-2 mt-2 w-full`}
    >
      <div className="flex items-center flex-wrap">
        <Heading as="h4">{song.title}</Heading>
        {alternativeTitles.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Badge variant="secondary" className="ml-2 mr-2">
                    {alternativeTitles.length}
                    {" "}
                    {t("songs:alternative-titles-count")}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {alternativeTitles.map(alt => (
                  <div key={alt.title}>{alt.title}</div>
                ))}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {song.iswc != null && song.iswc !== "" && (
        <p className="text-xs">
          <span className="font-semi-bold">ISWC:</span>
          {song.iswc}
        </p>
      )}
      <div className="w-full h-4" />
      <div className="flex flex-wrap">
        <div className="w-full lg:w-1/2">
          <p className="text-xs font-medium uppercase text-ink-secondary">
            <span>
              {t("songs:performed-by")}
              :
            </span>
            {" "}
            {performingArtists
              .filter(p => p.isdeleted !== true)
              .map(p => p.performingartistname.replace(/&#44;/g, ","))
              .join(", ")}
          </p>
        </div>
        <div className="w-full lg:w-1/2">
          <p className="text-xs font-medium uppercase text-ink-secondary">
            <span>
              {t("songs:duration")}
              :
            </span>
            {" "}
            {song.durationmins}
            {" "}
            {t("songs:minutes")}
            {" "}
            {song.durationsecs}
            {" "}
            {t("songs:seconds")}
          </p>
        </div>
      </div>
      <div className="w-full h-4" />
      <div>
        {song.containssamples && (
          <Badge variant="secondary" className="mr-1 uppercase">
            {t("songs:contains-samples")}
          </Badge>
        )}
        {song.isremix && (
          <Badge variant="secondary" className="mr-1 uppercase">
            {t("songs:is-remix")}
          </Badge>
        )}
      </div>
      <div className="w-full h-8" />
      <Heading as="h4">
        {t("songs:writers")}
        {" "}
        &amp;
        {" "}
        {t("songs:splits")}
      </Heading>
      <div className={`${isTunecore ? "bg-layer-inverse" : "bg-white"} p-4 py-2 rounded-lg mt-2`}>
        {writerSplits.map((w, i) => (
          <SongWriterSplit
            aac={aac}
            key={w.writercode}
            writer={w}
            last={i === writerSplits.length - 1}
          />
        ))}
      </div>

      <div className="w-full h-8" />
      <div className="w-full h-4" />
      <div>
        <div className="flex items-center">
          <Heading as="h4">
            {t("songs:recordings-subheader")}
          </Heading>
          <Badge variant="secondary" className="ml-2 mr-2 uppercase">
            {allRecordings.length}
          </Badge>
        </div>
        <div className="w-full h-4" />
        {!recordings.isSuccess && <LoadingSmall />}
        {recordings.isSuccess && (
          <div className={`${isTunecore ? "bg-layer-inverse" : "bg-white"} p-4 py-2 rounded-lg mt-2`}>
            {allRecordings.map((r, i) => (
              <SongRecording
                key={r.recordingidentifier !== "" ? r.recordingidentifier : i}
                recording={r}
                last={i === recordingsCount - 1}
              />
            ))}
            {allRecordings.length === 0 && (
              <p>{t("songs:no-recordings")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SongDetails;
